// /forge/forecast — per-applicant 120-day BOD completion forecast.
// Sourced from dash_ai_applicant_forecast (Unit 5, ml/pipeline/bod_completion.py).
//
// Columns: applicant, current_stage, days_in_stage, completion_probability,
// stall_category. Sort defaults by completion_probability descending so the
// most promising applicants surface first; stall_category filter lets a
// coordinator zero in on "dormant" or "slow" cohorts.

import { useEffect, useMemo, useState } from "react";

interface ForecastRow {
  applicant_id: string;
  completion_probability: number;
  stall_category: string | null;
  days_in_stage: number | null;
  current_stage: string | null;
  generated_at: string;
  model_version: string | null;
  applicant_name: string;
  application_result: string;
  pipeline_stage: string;
}

interface LatestRun {
  run_id: string;
  generated_at: string;
  model_version: string | null;
  scored_applicants: number;
}

interface Response {
  applicants: ForecastRow[];
  count: number;
  latest_run: LatestRun | null;
  note: string | null;
}

type StallFilter = "all" | "dormant" | "slow" | "onpace" | "accelerated";

const STALL_COLOR: Record<string, string> = {
  dormant: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200",
  slow: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200",
  onpace:
    "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200",
  accelerated: "bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-200",
};

export default function ApplicantForecast() {
  const [data, setData] = useState<Response | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [stallFilter, setStallFilter] = useState<StallFilter>("all");
  const [activeOnly, setActiveOnly] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const params = new URLSearchParams();
    params.set("limit", "500");
    if (!activeOnly) params.set("active_only", "0");
    if (stallFilter !== "all") params.set("stall_filter", stallFilter);
    fetch(`/api/forge/applicant-forecast?${params.toString()}`, {
      credentials: "same-origin",
    })
      .then((r) =>
        r.ok
          ? (r.json() as Promise<Response>)
          : Promise.reject(new Error(`HTTP ${r.status}`)),
      )
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      cancelled = true;
    };
  }, [stallFilter, activeOnly]);

  const counts = useMemo(() => {
    const rows = data?.applicants ?? [];
    const base: Record<StallFilter, number> = {
      all: rows.length,
      dormant: 0,
      slow: 0,
      onpace: 0,
      accelerated: 0,
    };
    for (const r of rows) {
      const k = (r.stall_category ?? "all") as StallFilter;
      if (k in base) base[k]++;
    }
    return base;
  }, [data]);

  if (error) {
    return (
      <div className="glass rounded-xl p-6 text-sm text-red-600 mx-4 md:mx-8">
        Failed to load: {error}
      </div>
    );
  }
  if (!data) {
    return (
      <div className="glass rounded-xl p-6 text-sm text-theme-muted mx-4 md:mx-8">
        Loading applicant forecast…
      </div>
    );
  }

  return (
    <div className="space-y-6 px-4 md:px-8 py-6 max-w-7xl mx-auto">
      <header>
        <p className="text-[11px] font-bold tracking-[0.14em] uppercase text-brand-adaptive mb-1">
          FORGE · Forecast
        </p>
        <h1 className="text-2xl font-bold text-theme-primary tracking-tight">
          Applicant BOD-completion forecast
        </h1>
        <p className="text-sm text-theme-secondary mt-1">
          Per-applicant 120-day BOD-approval probability from the XGBoost
          classifier (Unit 5). Use the stall category filter to surface dormant
          applicants for a coordinator call.
        </p>
      </header>

      {data.note && (
        <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-900/20 text-amber-900 dark:text-amber-200 text-sm px-4 py-3">
          {data.note}
        </div>
      )}

      <section className="glass rounded-2xl p-5 md:p-6">
        <div className="flex flex-wrap items-center gap-4 mb-4">
          <div className="text-xs text-theme-muted">
            <strong className="text-theme-primary">
              {data.count.toLocaleString()}
            </strong>{" "}
            applicants
            {data.latest_run && (
              <span className="ml-3">
                run {data.latest_run.generated_at.slice(0, 19)} UTC ·{" "}
                {data.latest_run.scored_applicants} scored ·{" "}
                {data.latest_run.model_version ?? "—"}
              </span>
            )}
          </div>
          <div className="ml-auto flex gap-2 text-xs">
            <label className="inline-flex items-center gap-1">
              <input
                type="checkbox"
                checked={activeOnly}
                onChange={(e) => setActiveOnly(e.target.checked)}
              />
              Active applicants only
            </label>
            <div className="border-l border-theme-subtle pl-2 ml-1 flex gap-1">
              {(
                [
                  "all",
                  "dormant",
                  "slow",
                  "onpace",
                  "accelerated",
                ] as StallFilter[]
              ).map((k) => (
                <button
                  key={k}
                  onClick={() => setStallFilter(k)}
                  className={`px-2 py-1 rounded ${
                    stallFilter === k
                      ? "bg-emerald-600 text-white"
                      : "border border-theme-subtle text-theme-secondary hover:bg-theme-overlay"
                  }`}
                >
                  {k}
                  {stallFilter === "all" &&
                    k !== "all" &&
                    counts[k] > 0 &&
                    ` (${counts[k]})`}
                </button>
              ))}
            </div>
          </div>
        </div>

        {data.applicants.length === 0 ? (
          <p className="text-sm text-theme-muted">
            No forecast rows. Run{" "}
            <code className="font-mono">
              uv run python -m pipeline.bod_completion --write
            </code>
            .
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-theme-muted border-b border-theme-subtle">
                  <th className="py-2 pr-3">Applicant</th>
                  <th className="py-2 px-3">Stage</th>
                  <th className="py-2 px-3 text-right">Days in stage</th>
                  <th className="py-2 px-3 text-right">P(BOD 120d)</th>
                  <th className="py-2 pl-3">Stall category</th>
                </tr>
              </thead>
              <tbody>
                {data.applicants.slice(0, 200).map((r) => {
                  const sc = r.stall_category ?? "unknown";
                  const pillCls =
                    STALL_COLOR[sc] ?? "bg-theme-overlay text-theme-muted";
                  return (
                    <tr
                      key={r.applicant_id}
                      className="hover:bg-theme-overlay/50 transition-colors"
                    >
                      <td className="py-2 pr-3 text-theme-primary">
                        <div className="font-medium truncate max-w-xs">
                          {r.applicant_name || r.applicant_id}
                        </div>
                        <div className="text-[10px] font-mono text-theme-muted">
                          {r.applicant_id}
                        </div>
                      </td>
                      <td className="py-2 px-3 text-xs text-theme-muted">
                        {r.pipeline_stage || r.current_stage || "—"}
                      </td>
                      <td className="py-2 px-3 text-right font-mono tabular-nums text-theme-muted">
                        {r.days_in_stage ?? "—"}
                      </td>
                      <td className="py-2 px-3 text-right font-mono tabular-nums text-theme-primary font-semibold">
                        {(r.completion_probability * 100).toFixed(1)}%
                      </td>
                      <td className="py-2 pl-3">
                        <span
                          className={`inline-block px-2 py-0.5 rounded text-[11px] font-semibold ${pillCls}`}
                        >
                          {sc}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {data.applicants.length > 200 && (
              <p className="text-xs text-theme-muted mt-3 italic">
                Showing top 200 of {data.applicants.length.toLocaleString()}.
              </p>
            )}
          </div>
        )}
      </section>

      <footer className="text-xs text-theme-muted">
        <p>
          <strong className="text-theme-secondary">Stall categories</strong>:{" "}
          <code className="font-mono">dormant</code> &lt;15% probability,{" "}
          <code className="font-mono">slow</code> 15–50%,{" "}
          <code className="font-mono">onpace</code> 50–70%,{" "}
          <code className="font-mono">accelerated</code> ≥70%. Source:{" "}
          <code className="font-mono">dash_ai_applicant_forecast</code>. Refit
          by <code className="font-mono">data-sync-ml-nightly</code>.
        </p>
      </footer>
    </div>
  );
}
